export type CrossTabEvent =
  | 'sync:tables'
  | 'sync:table-groups'
  | 'sync:reservations'
  | 'sync:shifts'
  | 'sync:customers'
  | 'sync:pending'
  | 'sync:all';

interface CrossTabMessage {
  type: CrossTabEvent;
  payload?: unknown;
}

const CHANNEL_NAME = 'tablein-sync';
const STORAGE_KEY = 'tablein-sync-message';

const broadcast = typeof window !== 'undefined' && 'BroadcastChannel' in window
  ? new BroadcastChannel(CHANNEL_NAME)
  : null;

function emitLocally(message: CrossTabMessage) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(message.type, { detail: message.payload }));
}

export function publishCrossTab(message: CrossTabMessage) {
  emitLocally(message);

  if (broadcast) {
    broadcast.postMessage(message);
    return;
  }

  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...message, ts: Date.now() })
    );
  } catch (error) {
    console.warn('[crossTab] Failed to write storage event', error);
  }
}

export function subscribeCrossTab(handler: (message: CrossTabMessage) => void) {
  // Local (same-tab) listener so publisher also gets the event
  const localListener = (event: Event) => {
    const custom = event as CustomEvent;
    handler({ type: event.type as CrossTabEvent, payload: custom.detail });
  };
  if (typeof window !== 'undefined') {
    window.addEventListener('sync:tables', localListener);
    window.addEventListener('sync:table-groups', localListener);
    window.addEventListener('sync:reservations', localListener);
    window.addEventListener('sync:shifts', localListener);
    window.addEventListener('sync:customers', localListener);
    window.addEventListener('sync:pending', localListener);
    window.addEventListener('sync:all', localListener);
  }

  if (broadcast) {
    const listener = (event: MessageEvent<CrossTabMessage>) => {
      if (event.data?.type) handler(event.data);
    };
    broadcast.addEventListener('message', listener);
    return () => {
      broadcast.removeEventListener('message', listener);
      if (typeof window !== 'undefined') {
        window.removeEventListener('sync:tables', localListener);
        window.removeEventListener('sync:table-groups', localListener);
        window.removeEventListener('sync:reservations', localListener);
        window.removeEventListener('sync:shifts', localListener);
        window.removeEventListener('sync:customers', localListener);
        window.removeEventListener('sync:pending', localListener);
        window.removeEventListener('sync:all', localListener);
      }
    };
  }

  const storageListener = (event: StorageEvent) => {
    if (event.key !== STORAGE_KEY || !event.newValue) return;
    try {
      const parsed = JSON.parse(event.newValue) as CrossTabMessage;
      if (parsed?.type) {
        handler(parsed);
      }
    } catch (error) {
      console.warn('[crossTab] Failed to parse storage message', error);
    } finally {
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch {}
    }
  };

  window.addEventListener('storage', storageListener);
  return () => {
    window.removeEventListener('storage', storageListener);
    if (typeof window !== 'undefined') {
      window.removeEventListener('sync:tables', localListener);
      window.removeEventListener('sync:table-groups', localListener);
      window.removeEventListener('sync:reservations', localListener);
      window.removeEventListener('sync:shifts', localListener);
      window.removeEventListener('sync:customers', localListener);
      window.removeEventListener('sync:pending', localListener);
      window.removeEventListener('sync:all', localListener);
    }
  };
}
